import { useEffect, useRef, useState, type PointerEvent, type ReactElement } from 'react';
import { Play } from 'lucide-react';
import { useArrangementTakes, useDeleteTake } from '@/lib/queries/takes';
import { formatMmSsFromMs } from '@/lib/format-time';
import { useAuthedBlobUrl } from '@/lib/hooks/use-authed-blob-url';
import { usePlayback } from '@/lib/hooks/use-playback';
import { Card } from '@/components/atoms/card';
import { Micro } from '@/components/atoms/micro';
import { PlaybackDock } from '@/components/atoms/playback-dock';
import type { Take } from '@/lib/types';

const LONG_PRESS_MS = 600;

function formatDateLong(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type TakeRowProps = {
  take: Take;
  onLongPress: (take: Take) => void;
  isSelected: boolean;
  onSelect: (take: Take) => void;
  onPlay: (take: Take) => void;
};

function TakeRow({ take, onLongPress, isSelected, onSelect, onPlay }: TakeRowProps): ReactElement {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressedRef = useRef<boolean>(false);

  const startTimer = (): void => {
    longPressedRef.current = false;
    timerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      onLongPress(take);
    }, LONG_PRESS_MS);
  };

  const cancelTimer = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    startTimer();
  };

  const handlePointerUp = (): void => {
    cancelTimer();
    if (!longPressedRef.current) {
      onSelect(take);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Delete') {
      event.preventDefault();
      onLongPress(take);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(take);
    }
  };

  const recordedLabel = formatDateLong(take.recordedAt);
  return (
    <li className="flex items-stretch gap-2">
      <button
        type="button"
        aria-label={`Take recorded ${recordedLabel}`}
        aria-pressed={isSelected}
        className={`flex flex-1 items-center justify-between gap-3 rounded-[var(--r-sm)] border px-3 py-3 text-left transition-colors ${
          isSelected
            ? 'border-[var(--brand)] bg-[var(--bg-soft)]'
            : 'border-[var(--rule)] bg-[var(--bg-surface)] hover:border-[var(--brand)]'
        }`}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={cancelTimer}
        onPointerCancel={cancelTimer}
        onKeyDown={handleKeyDown}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-[var(--ink)]">{recordedLabel}</span>
          <span className="text-xs text-[var(--ink-3)]">{take.label.prettyName}</span>
        </div>
        <span className="font-mono text-xs text-[var(--ink-3)] tabular-nums">
          {formatMmSsFromMs(take.durationSeconds * 1000)}
        </span>
      </button>
      <button
        type="button"
        aria-label={`Play take from ${recordedLabel}`}
        onClick={() => onPlay(take)}
        className="inline-flex w-11 shrink-0 items-center justify-center rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] text-[var(--ink)] transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]"
      >
        <Play className="h-4 w-4 fill-current" />
      </button>
    </li>
  );
}

type DeleteConfirmProps = {
  open: boolean;
  takeLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
};

function DeleteConfirm({ open, takeLabel, onConfirm, onCancel }: DeleteConfirmProps): ReactElement | null {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete this take"
      className="fixed inset-0 z-[70] flex items-center justify-center"
    >
      <button
        type="button"
        aria-label="Dismiss delete confirm"
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative max-w-sm rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-6 shadow-2xl">
        <h2 className="mb-2 text-base font-semibold text-[var(--ink)]">Delete this take?</h2>
        <p className="mb-4 text-sm text-[var(--ink-3)]">
          {takeLabel} — the audio file will be removed permanently.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-9 items-center justify-center rounded-[var(--r-sm)] bg-[var(--bg-soft)] px-3 text-sm font-semibold text-[var(--ink)] hover:bg-[var(--rule)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-9 items-center justify-center rounded-[var(--r-sm)] bg-[var(--err,#b91c1c)] px-3 text-sm font-semibold text-white hover:opacity-90"
          >
            Confirm delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function TakesSection({ arrangementId }: { arrangementId: string }): ReactElement {
  const { data } = useArrangementTakes(arrangementId);
  const deleteTake = useDeleteTake(arrangementId);
  const [selectedTakeId, setSelectedTakeId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Take | null>(null);
  const [playingTake, setPlayingTake] = useState<Take | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const playback = usePlayback(audioRef);

  // Take audio is access-controlled, so it must be fetched with the bearer
  // token via the api-client and played from an object URL — a native
  // <audio src="/api/..."> request can't carry the token and would 401.
  const { url: audioBlobUrl } = useAuthedBlobUrl(playingTake?.audioUrl ?? null);

  const { load, play } = playback;
  useEffect(() => {
    if (audioBlobUrl === null) return;
    load(audioBlobUrl);
    void play();
  }, [audioBlobUrl, load, play]);

  const takes = data?.content ?? [];

  const playTake = (take: Take): void => {
    setPlayingTake(take);
    setSelectedTakeId(take.takeId);
  };

  const dockVisible = playingTake !== null && playback.status !== 'idle';

  return (
    <section aria-label="Takes">
      <Card padding="lg">
        <div className="mb-4 flex items-center justify-between">
          <Micro>Takes</Micro>
        </div>
        {takes.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--ink-3)]">No takes recorded yet.</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-2">
            {takes.map((take) => (
              <TakeRow
                key={take.takeId}
                take={take}
                onLongPress={(t) => setPendingDelete(t)}
                isSelected={selectedTakeId === take.takeId}
                onSelect={(t) => setSelectedTakeId(t.takeId)}
                onPlay={playTake}
              />
            ))}
          </ul>
        )}
      </Card>
      <audio ref={audioRef} aria-label="take-audio" preload="metadata" className="hidden" />
      {dockVisible && playingTake && (
        <PlaybackDock
          status={playback.status === 'playing' ? 'playing' : 'paused'}
          title={`Take recorded ${formatDateLong(playingTake.recordedAt)}`}
          caption={playingTake.label.prettyName}
          currentMs={playback.currentMs}
          durationMs={playback.durationMs}
          onPlay={() => void playback.play()}
          onPause={() => playback.pause()}
          onSeek={(ms) => playback.seekTo(ms)}
        />
      )}
      <DeleteConfirm
        open={pendingDelete !== null}
        takeLabel={pendingDelete ? formatDateLong(pendingDelete.recordedAt) : ''}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) {
            deleteTake.mutate({ takeId: pendingDelete.takeId });
            setPendingDelete(null);
          }
        }}
      />
    </section>
  );
}
